import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Use cached user from localStorage instantly to avoid blank screen on refresh
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (cached && token) return JSON.parse(cached);
    } catch (e) {}
    return null;
  });
  const [loading, setLoading] = useState(() => {
    // If we have cached user, skip loading state entirely
    const cached = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    return !(cached && token);
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } catch (err) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      setError(null);
      console.log('AuthContext: Calling login API...');
      const response = await authAPI.login({ email, password });
      console.log('AuthContext: API response received:', response);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      console.log('AuthContext: Login successful, user set');
      return { success: true, user };
    } catch (err) {
      console.error('AuthContext: Login error:', err);
      console.error('AuthContext: Error response:', err.response);
      
      // Check if it's a disabled account error
      if (err.response?.status === 403 && err.response?.data?.message) {
        const errorMessage = err.response.data.message;
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
      
      const errorMessage = err.response?.data?.error || err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signup = async (data) => {
    try {
      setError(null);
      const response = await authAPI.signup(data);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      return { success: true, user };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Signup failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.replace('/login');
    window.location.reload();
  };

  const updateUser = async (userData) => {
    try {
      setError(null);
      const response = await authAPI.updateUser(user.userId, userData);
      const updatedUser = response.data.user;
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true, user: updatedUser };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Update failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const updateCompany = async (companyData) => {
    try {
      setError(null);
      const response = await authAPI.updateCompany(companyData);
      
      // Update user object with new company name
      const updatedUser = {
        ...user,
        companyName: response.data.companyName
      };
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true, companyName: response.data.companyName };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Company update failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    updateUser,
    updateCompany,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
